/**
 * Based on Manak-main/src/pages/Admin.tsx
 *
 * Layout preserved:
 *  - Header: greeting + gradient separator + TODAY + date
 *  - Stats cards row (Total, Present, On Leave)
 *  - Mobile: leave sidebar above attendance table
 *  - Desktop: 5-col table + 2-col leave sidebar grid
 *  - selectedEmployee → AttendanceCalendarView drill-down
 *
 * All hardcoded data replaced with real Redux state.
 */

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { isAdmin } from '../redux/types/authTypes';
import toast from 'react-hot-toast';
import { BsPersonFillCheck } from 'react-icons/bs';
import { FaPersonWalkingArrowRight } from 'react-icons/fa6';
import { FaUsers, FaTag } from 'react-icons/fa';
import { MdAdd, MdClose, MdFileDownload, MdExpandMore } from 'react-icons/md';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import {
  fetchTeamAttendance,
  fetchTeamLeaveRequests,
  reviewLeaveRequest,
  fetchAllAttendance,
  fetchAllLeaveRequests,
  fetchLeaveTypes,
  fetchTeamYearlySummary,
  fetchAllYearlySummary,
} from '../redux/actions/attendanceActions';
import type { CalendarDay, LeaveType, EmployeeYearlySummary } from '../redux/types/attendanceTypes';
import AttendanceTableView, { type FlatRecord, type DailyEmployee } from '../components/Attendance/AttendanceTableView';
import LeaveRequestSidebar from '../components/Attendance/LeaveRequestSidebar';
import AttendanceCalendarView from '../components/Attendance/AttendanceCalendarView';
import EmployeeLeaveHistory from '../components/Attendance/EmployeeLeaveHistory';
import axiosInstance from '../axios';
import CustomSelect, { yearOptions } from '../components/Attendance/CustomSelect';

// ── Leave Types Manager (admin only) ─────────────────────────────────────────

const LeaveTypesManager: React.FC<{ leaveTypes: LeaveType[]; onRefresh: () => void }> = ({ leaveTypes, onRefresh }) => {
  const [name, setName] = useState('');
  const [days, setDays] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !days) return;
    setSaving(true);
    try {
      await axiosInstance.post('/v1/admin/leave-types', { name: name.trim(), defaultDays: Number(days) });
      toast.success('Leave type added');
      setName(''); setDays('');
      onRefresh();
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? 'Failed to add leave type');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    setDeleting(id);
    try {
      await axiosInstance.delete(`/v1/admin/leave-types/${id}`);
      toast.success('Leave type deleted');
      onRefresh();
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? 'Failed to delete leave type');
    } finally { setDeleting(null); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <FaTag className="text-[#2D328F] text-lg" />
        <h2 className="text-lg font-bold text-gray-800">Leave Types</h2>
      </div>

      {/* Add form */}
      <form onSubmit={handleAdd} className="flex flex-wrap gap-3 mb-4">
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Sick Leave"
          className="flex-1 min-w-[140px] border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2D328F]/20"
        />
        <input
          type="number"
          value={days}
          onChange={e => setDays(e.target.value)}
          placeholder="Days"
          min={1}
          className="w-24 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2D328F]/20"
        />
        <button type="submit" disabled={saving || !name.trim() || !days}
          className="flex items-center gap-1.5 bg-[#2D328F] hover:bg-[#232880] disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors">
          <MdAdd /> Add
        </button>
      </form>

      {/* Existing types */}
      {leaveTypes.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-2">No leave types yet. Add one above.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {leaveTypes.map(t => (
            <div key={t.id} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-3 py-1.5">
              <span className="text-sm text-gray-700 font-medium">{t.name}</span>
              <span className="text-xs text-gray-400">{t.defaultDays}d</span>
              <button onClick={() => handleDelete(t.id)} disabled={deleting === t.id}
                className="text-gray-400 hover:text-red-500 transition-colors ml-1 disabled:opacity-50">
                <MdClose className="text-sm" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Stats card (from Manak StateCard.tsx) ────────────────────────────────────

const StatsCard: React.FC<{
  title: string;
  value: string | number;
  color: string;
  titleColor?: string;
  icon?: React.ReactNode;
  iconColor?: string;
  iconBg?: string;
  shadow?: string;
}> = ({ title, value, color, titleColor, icon, iconColor, iconBg, shadow = '' }) => (
  <div className={`bg-white p-2 xl:p-6 md:p-4 rounded-xl w-full ${shadow}`}>
    <div className="flex items-start justify-between">
      <p className={`text-sm xl:text-2xl md:text-xl font-semibold mb-4 ${titleColor}`}>{title}</p>
      {icon && (
        <div className={`text-xs xl:text-2xl md:text-xl w-6 h-6 md:w-10 md:h-10 xl:w-12 xl:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${iconBg} ${iconColor}`}>
          {icon}
        </div>
      )}
    </div>
    <h2 className={`text-xl xl:text-4xl md:text-3xl font-bold ${color}`}>{value}</h2>
  </div>
);

// ── Export dropdown (per-employee xlsx) ──────────────────────────────────────

const ExportDropdown: React.FC<{
  employeeId: string;
  year: number;
  month?: number;
}> = ({ employeeId, year, month }) => {
  const [open,    setOpen]    = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const doExport = async (y: number, m?: number) => {
    setOpen(false);
    setLoading(true);
    try {
      const params = new URLSearchParams({ employeeId, year: String(y) });
      if (m) params.append('month', String(m));
      const res = await axiosInstance.get(`/v1/admin/attendance/export?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement('a');
      a.href    = url;
      const disp = res.headers['content-disposition'] as string | undefined;
      const match = disp?.match(/filename="(.+)"/);
      a.download = match?.[1] ?? `attendance_${y}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        onClick={() => setOpen(o => !o)}
        disabled={loading}
        className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 transition-colors disabled:opacity-50"
      >
        {loading ? '…' : <MdFileDownload className="text-sm" />}
        Export
        <MdExpandMore className={`text-sm transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-100 rounded-xl shadow-lg z-20 py-1">
          <button
            onClick={() => doExport(year, month)}
            className="w-full text-left px-4 py-2 text-xs text-gray-700 hover:bg-gray-50"
          >
            This Month
          </button>
          <button
            onClick={() => doExport(year)}
            className="w-full text-left px-4 py-2 text-xs text-gray-700 hover:bg-gray-50"
          >
            This Year
          </button>
        </div>
      )}
    </div>
  );
};

// ── Yearly team summary view ───────────────────────────────────────────────────

const MONTH_NAMES_SHORT_MANAGE = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const YearlyTeamView: React.FC<{
  summaries: EmployeeYearlySummary[];
  loading: boolean;
  year: number;
  month: number;
  onYearChange: (y: number) => void;
}> = ({ summaries, loading, year, month, onYearChange }) => {
  const [expandedId, setExpandedId] = React.useState<string | null>(null);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5">
      {/* Year selector */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-800">Yearly Summary</h2>
        <CustomSelect
          value={year}
          options={yearOptions(1900, new Date().getFullYear())}
          onChange={onYearChange}
        />
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-14 rounded-xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : summaries.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-8">No data.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-xs text-gray-400 uppercase tracking-wide">
                <th className="text-left pb-2 pr-4 font-semibold">Employee</th>
                <th className="text-left pb-2 pr-3 font-semibold hidden sm:table-cell">Designation</th>
                <th className="text-center pb-2 px-2 font-semibold text-emerald-600">Present</th>
                <th className="text-center pb-2 px-2 font-semibold text-red-500">Absent</th>
                <th className="text-center pb-2 px-2 font-semibold text-sky-600">Half</th>
                <th className="text-center pb-2 px-2 font-semibold text-amber-600">Leave</th>
                <th className="text-center pb-2 px-2 font-semibold text-purple-600">Hrs</th>
                <th className="text-right pb-2 pl-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {summaries.map(emp => (
                <React.Fragment key={emp.employeeId}>
                  <tr className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-2.5 pr-4">
                      <p className="font-semibold text-gray-900 text-xs">{emp.employeeName}</p>
                    </td>
                    <td className="py-2.5 pr-3 text-gray-500 text-xs hidden sm:table-cell">{emp.designation}</td>
                    <td className="py-2.5 px-2 text-center font-semibold text-emerald-700">{emp.summary.totals.present}</td>
                    <td className="py-2.5 px-2 text-center font-semibold text-red-500">{emp.summary.totals.absent}</td>
                    <td className="py-2.5 px-2 text-center font-semibold text-sky-600">{emp.summary.totals.halfDay}</td>
                    <td className="py-2.5 px-2 text-center font-semibold text-amber-600">{emp.summary.totals.onLeave}</td>
                    <td className="py-2.5 px-2 text-center font-semibold text-purple-600">{emp.summary.totals.totalWorkingHours.toFixed(0)}h</td>
                    <td className="py-2.5 pl-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setExpandedId(id => id === emp.employeeId ? null : emp.employeeId)}
                          className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 transition-colors"
                        >
                          {expandedId === emp.employeeId ? 'Hide' : 'View'}
                        </button>
                        <ExportDropdown employeeId={emp.employeeId} year={year} month={month} />
                      </div>
                    </td>
                  </tr>
                  {/* Expanded month breakdown */}
                  {expandedId === emp.employeeId && (
                    <tr>
                      <td colSpan={8} className="pb-3 pt-1 px-2">
                        <div className="bg-gray-50 rounded-xl p-3 overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-gray-400 uppercase tracking-wide">
                                <th className="text-left pb-1 pr-3">Month</th>
                                <th className="text-center pb-1 px-2 text-emerald-600">Present</th>
                                <th className="text-center pb-1 px-2 text-red-500">Absent</th>
                                <th className="text-center pb-1 px-2 text-sky-600">Half</th>
                                <th className="text-center pb-1 px-2 text-amber-600">Leave</th>
                                <th className="text-center pb-1 px-2 text-purple-600">Hrs</th>
                              </tr>
                            </thead>
                            <tbody>
                              {emp.summary.monthly.map(m => (
                                <tr key={m.month} className="border-t border-gray-100">
                                  <td className="py-1 pr-3 font-medium text-gray-700">{MONTH_NAMES_SHORT_MANAGE[m.month - 1]}</td>
                                  <td className="py-1 px-2 text-center text-emerald-700">{m.present}</td>
                                  <td className="py-1 px-2 text-center text-red-500">{m.absent}</td>
                                  <td className="py-1 px-2 text-center text-sky-600">{m.halfDay}</td>
                                  <td className="py-1 px-2 text-center text-amber-600">{m.onLeave}</td>
                                  <td className="py-1 px-2 text-center text-purple-600">{m.totalWorkingHours.toFixed(0)}h</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const ManageAttendance: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);
  const {
    teamAttendance,
    teamLeaveRequests,
    allAttendance,
    totalEmployees,
    allLeaveRequests,
    leaveTypes,
    loading,
    teamYearlySummary,
    yearlySummaryLoading,
  } = useAppSelector(state => state.attendance);

  const admin = isAdmin(user);

  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;

  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [viewTab,    setViewTab]    = useState<'daily' | 'yearly'>('daily');
  const [yearlyYear, setYearlyYear] = useState(now.getFullYear());

  // Manager: date for the daily table view (managed here so we can compute teamDailyData)
  const [managerDate, setManagerDate] = useState(todayStr);

  // Admin: daily status for all employees
  const [dailyData,        setDailyData]        = useState<any[]>([]);
  const [dailyDate,        setDailyDate]        = useState(todayStr);
  const [dailyLoading,     setDailyLoading]     = useState(false);

  // Employee calendar drill-down
  const [selectedEmployee, setSelectedEmployee] = useState<{
    id: string;
    name: string;
    designation: string;
    profilePicture?: string | null;
  } | null>(null);
  const [drillCalendar,    setDrillCalendar]    = useState<CalendarDay[]>([]);
  const [drillCalLoading,  setDrillCalLoading]  = useState(false);
  const [drillYear,        setDrillYear]        = useState(now.getFullYear());
  const [drillMonth,       setDrillMonth]       = useState(now.getMonth() + 1);
  const [drillLeaves,      setDrillLeaves]      = useState<any[]>([]);

  // Sync sidebar height to table height
  const tableColRef  = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState<number | null>(null);

  useEffect(() => {
    const el = tableColRef.current;
    if (!el) return;
    const obs = new ResizeObserver(entries => setTableHeight(entries[0].contentRect.height));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const fetchDailyData = (date: string) => {
    setDailyLoading(true);
    axiosInstance.get(`/v1/admin/attendance/daily?date=${date}`)
      .then(r => setDailyData(r.data.data))
      .catch(() => setDailyData([]))
      .finally(() => setDailyLoading(false));
  };

  useEffect(() => {
    if (admin) {
      dispatch(fetchLeaveTypes());
      fetchDailyData(dailyDate);
    }
  }, [admin]);

  useEffect(() => {
    if (admin) {
      dispatch(fetchAllAttendance({ year, month }));
      dispatch(fetchAllLeaveRequests({}));
    } else if (user?.id) {
      dispatch(fetchTeamAttendance({ managerId: user.id, year, month }));
      dispatch(fetchTeamLeaveRequests({ managerId: user.id }));
    }
    setSelectedEmployee(null);
  }, [year, month]);

  useEffect(() => {
    if (viewTab !== 'yearly') return;
    if (admin) {
      dispatch(fetchAllYearlySummary({ year: yearlyYear }));
    } else if (user?.id) {
      dispatch(fetchTeamYearlySummary({ managerId: user.id, year: yearlyYear }));
    }
  }, [viewTab, yearlyYear, admin, user?.id]);

  const handleMonthChange = (y: number, m: number) => { setYear(y); setMonth(m); };

  const openEmployeeDrill = (id: string, name: string, designation: string, profilePicture?: string | null) => {
    setDrillYear(now.getFullYear());
    setDrillMonth(now.getMonth() + 1);
    setSelectedEmployee({ id, name, designation, profilePicture });
    setDrillLeaves([]);
    axiosInstance.get(`/v1/admin/attendance/leave-history/${id}`)
      .then(r => setDrillLeaves(r.data.data))
      .catch(() => {});
  };

  const handleDailyDateChange = (date: string) => {
    setDailyDate(date);
    fetchDailyData(date);
  };

  const handleReview = async (requestId: string, status: 'APPROVED' | 'REJECTED', reviewNote: string) => {
    const result = await dispatch(reviewLeaveRequest({ requestId, status, reviewNote }));
    if (reviewLeaveRequest.fulfilled.match(result)) {
      toast.success(`Leave request ${status.toLowerCase()}`);
      if (admin) dispatch(fetchAllLeaveRequests({}));
      else if (user?.id) dispatch(fetchTeamLeaveRequests({ managerId: user.id }));
    } else {
      toast.error((result.payload as string) ?? 'Failed to review request');
    }
  };

  // Flatten team attendance — still used for stats
  const teamFlatRecords = useMemo<FlatRecord[]>(() =>
    teamAttendance.flatMap(m =>
      m.attendance.map(a => ({
        ...a,
        employee: { fullname: m.fullname, employeeId: m.employeeId, designation: m.designation },
      }))
    ), [teamAttendance]
  );

  // Daily snapshot for the manager table — all team members shown, not just those with records
  const teamDailyData = useMemo<DailyEmployee[]>(() =>
    teamAttendance.map(m => {
      const record = (m as any).attendance.find(
        (a: any) => a.date.slice(0, 10) === managerDate
      );
      const weekday = new Date(managerDate + 'T00:00:00').getDay();
      let status: string;
      if (record) {
        status = record.status;
      } else if (weekday === 0) {
        status = 'WEEKEND';
      } else if (managerDate <= todayStr) {
        status = 'ABSENT';
      } else {
        status = 'UPCOMING';
      }
      return {
        id:             m.id,
        fullname:       m.fullname,
        employeeId:     m.employeeId,
        designation:    m.designation,
        profilePicture: (m as any).profilePicture ?? null,
        status,
        checkInTime:    record?.checkInTime  ?? null,
        checkOutTime:   record?.checkOutTime ?? null,
        workingHours:   record?.workingHours ?? null,
      };
    }), [teamAttendance, managerDate, todayStr]
  );

  const handleManagerDateChange = (date: string) => {
    setManagerDate(date);
    const d = new Date(date + 'T00:00:00');
    const newYear = d.getFullYear();
    const newMonth = d.getMonth() + 1;
    if (newYear !== year || newMonth !== month) {
      // Cross-month: update state → useEffect fires → fetchTeamAttendance
      setYear(newYear);
      setMonth(newMonth);
    } else if (user?.id) {
      // Same month: useEffect won't fire (deps unchanged), so dispatch directly
      dispatch(fetchTeamAttendance({ managerId: user.id, year: newYear, month: newMonth }));
    }
  };

  // Fetch calendar with leave data whenever drill-down employee or month changes
  useEffect(() => {
    if (!selectedEmployee) return;
    setDrillCalLoading(true);
    const url = admin
      ? `/v1/admin/attendance/calendar/${selectedEmployee.id}/${drillYear}/${drillMonth}`
      : `/v1/admin/attendance/calendar/${selectedEmployee.id}/${drillYear}/${drillMonth}`;
    axiosInstance.get(url)
      .then(res => setDrillCalendar(res.data.data))
      .catch(() => setDrillCalendar([]))
      .finally(() => setDrillCalLoading(false));
  }, [selectedEmployee?.id, drillYear, drillMonth]);

  // Stats — today's count from the already-loaded month data
  const stats = useMemo(() => {
    const source   = admin ? (allAttendance as FlatRecord[]) : teamFlatRecords;
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const todayRec = source.filter(r => r.date.slice(0, 10) === todayStr);
    return {
      total:   admin ? totalEmployees : teamAttendance.length,
      present: todayRec.filter(r => r.status === 'PRESENT' || r.status === 'HALF_DAY').length,
      onLeave: todayRec.filter(r => r.status === 'ON_LEAVE').length,
    };
  }, [allAttendance, teamFlatRecords, admin, teamAttendance, totalEmployees]);

  const leaveRequests  = admin ? allLeaveRequests : teamLeaveRequests;

  const todayFormatted = now.toLocaleDateString('en-IN', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
  const firstName = (user as any)?.fullname?.split(' ')[0] ?? (user as any)?.name ?? (admin ? 'Admin' : 'Manager');

  // ── Main dashboard view ───────────────────────────────────────────────────
  return (
    <ComponentWrapper
      title={selectedEmployee ? selectedEmployee.name : `Hey, ${admin ? 'Admin' : firstName}`}
      buttonOne={
        !selectedEmployee ? (
          <div className="flex items-center gap-2">
            <div className="hidden md:block w-[1px] xl:w-[2px] h-8 md:h-10 xl:h-14 bg-gradient-to-b from-white via-[#19C6DF] to-white rounded-full mr-3 sm:mr-4" />
            <div className="text-right">
              <p className="text-[10px] xl:text-base text-[#19C6DF] font-semibold">TODAY</p>
              <p className="text-sm xl:text-2xl md:text-xl text-[#2D328F] font-semibold">{todayFormatted}</p>
            </div>
          </div>
        ) : undefined
      }
    >

        {/* ── Calendar drill-down + leave history ── */}
        {selectedEmployee && (
          <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
            {/* Calendar */}
            <div className="md:col-span-5">
              <AttendanceCalendarView
                onBack={() => { setSelectedEmployee(null); setDrillCalendar([]); setDrillLeaves([]); }}
                employeeId={selectedEmployee.id}
                employeeName={selectedEmployee.name}
                designation={selectedEmployee.designation}
                profilePicture={selectedEmployee.profilePicture}
                calendar={drillCalendar}
                year={drillYear}
                month={drillMonth}
                loading={drillCalLoading}
                onMonthChange={(y, m) => { setDrillYear(y); setDrillMonth(m); }}
              />
            </div>
            {/* Leave history sidebar */}
            <div className="md:col-span-2">
              <EmployeeLeaveHistory requests={drillLeaves} />
            </div>
          </div>
        )}

        {selectedEmployee ? null : (<>

        {/* View tab bar */}
        <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit mb-4">
          {(['daily', 'yearly'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setViewTab(tab)}
              className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors capitalize ${
                viewTab === tab
                  ? 'bg-white shadow text-gray-900'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {viewTab === 'yearly' && (
          <YearlyTeamView
            summaries={teamYearlySummary}
            loading={yearlySummaryLoading}
            year={yearlyYear}
            month={month}
            onYearChange={setYearlyYear}
          />
        )}

        {viewTab === 'daily' && (<>

        {/* Admin: leave type management — hidden until feature is ready */}
        {false && admin && (
          <LeaveTypesManager
            leaveTypes={leaveTypes}
            onRefresh={() => dispatch(fetchLeaveTypes())}
          />
        )}

        {/* Non-admin with no team members */}
        {!admin && !loading && teamAttendance.length === 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 flex flex-col items-center justify-center text-center gap-3">
            <div className="text-5xl">👥</div>
            <p className="text-gray-700 font-semibold">No team members found</p>
            <p className="text-sm text-gray-400">
              You don't have any employees assigned to you yet. Ask your admin to set you as a manager for team members.
            </p>
          </div>
        )}

        {/* Desktop stats row */}
        <div className="hidden md:flex gap-4 pb-2 mb-8">
          <StatsCard
            title={admin ? 'Total Employees' : 'Team Size'}
            value={stats.total}
            color="text-[#066E79]"
            titleColor="text-[#198591]"
            icon={<FaUsers />}
            iconColor="text-[#198591]"
            iconBg="bg-[#E2FCFF]"
            shadow="shadow-[0_6px_12px_rgba(200,235,245,1)]"
          />
          <StatsCard
            title="Present Today"
            value={stats.present}
            color="text-[#28A745]"
            titleColor="text-[#33D959]"
            icon={<BsPersonFillCheck />}
            iconColor="text-[#28A745]"
            iconBg="bg-[#F0FFF3]"
            shadow="shadow-[0_6px_12px_rgba(205,240,210,1)]"
          />
          <StatsCard
            title="On Leave Today"
            value={stats.onLeave}
            color="text-[#FFA500]"
            titleColor="text-[#FFA500]"
            icon={<FaPersonWalkingArrowRight />}
            iconColor="text-[#FFBB3F]"
            iconBg="bg-[#FFF9ED]"
            shadow="shadow-[0_8px_12px_rgba(215,210,195,1)]"
          />
        </div>

        {/* Mobile stats */}
        <div className="md:hidden flex flex-col gap-3 mb-6">
          <StatsCard
            title={admin ? 'Total Employees' : 'Team Size'}
            value={stats.total}
            color="text-[#066E79]"
            titleColor="text-[#198591]"
            icon={<FaUsers />}
            iconColor="text-[#198591]"
            iconBg="bg-[#E2FCFF]"
            shadow="shadow-[0_6px_12px_rgba(226,252,255,0.9)]"
          />
          <div className="flex gap-3">
            <div className="flex-1">
              <StatsCard
                title="Present"
                value={stats.present}
                color="text-[#28A745]"
                titleColor="text-[#33D959]"
                icon={<BsPersonFillCheck />}
                iconColor="text-[#28A745]"
                iconBg="bg-[#F0FFF3]"
                shadow="shadow-[0_6px_12px_rgba(240,255,243,0.9)]"
              />
            </div>
            <div className="flex-1">
              <StatsCard
                title="On Leave"
                value={stats.onLeave}
                color="text-[#FFA500]"
                titleColor="text-[#FFA500]"
                icon={<FaPersonWalkingArrowRight />}
                iconColor="text-[#FFBB3F]"
                iconBg="bg-[#FFF9ED]"
                shadow="shadow-[0_8px_12px_rgba(255,249,237,0.9)]"
              />
            </div>
          </div>
        </div>

        {/* Mobile: leave sidebar appears above the table (mirrors Manak) */}
        <div className="md:hidden mb-8">
          <LeaveRequestSidebar
            requests={leaveRequests}
            onReview={handleReview}
            loading={loading}
          />
        </div>

        {/* Content grid */}
        <div className="grid grid-cols-1 md:grid-cols-7 gap-2 md:gap-4">
          <div className="md:col-span-5 md:self-start" ref={tableColRef}>
            {admin ? (
              <AttendanceTableView
                dailyData={dailyData}
                selectedDate={dailyDate}
                onDateChange={handleDailyDateChange}
                loading={dailyLoading}
                onEmployeeClick={openEmployeeDrill}
              />
            ) : (
              <AttendanceTableView
                dailyData={teamDailyData}
                selectedDate={managerDate}
                onDateChange={handleManagerDateChange}
                loading={loading}
                onEmployeeClick={openEmployeeDrill}
              />
            )}
          </div>

          {/* Desktop leave sidebar — height locked to table height */}
          <div
            className="hidden md:flex md:col-span-2 xl:col-span-2"
            style={tableHeight ? { height: tableHeight } : {}}
          >
            <LeaveRequestSidebar
              requests={leaveRequests}
              onReview={handleReview}
              loading={loading}
            />
          </div>
        </div>

        </>)}

        </>)}

    </ComponentWrapper>
  );
};

export default ManageAttendance;
